import type { gameResult } from '../../models/gameUpdate';

export interface ScoredResultRow {
  docId: string;
  result: gameResult;
}

export interface DuplicateResultGroup {
  kept: string;
  dropped: string[];
  matchKey: string;
}

function sortedPairKey(countryA: string, countryB: string): string {
  return [countryA, countryB].sort().join(':');
}

export function dedupeMatchKey(row: ScoredResultRow): string {
  const { result } = row;
  if (result.matchId) return `matchId:${result.matchId}`;
  const pair = sortedPairKey(String(result.countryA), String(result.countryB));
  const md = result.matchday ?? 0;
  return `pair:${pair}:md${md}`;
}

function rowPriority(docId: string, result: gameResult): number {
  if (result.matchId && docId === `api-${result.matchId}`) return 3;
  if (result.source === 'api-football') return 2;
  return 1;
}

function rowRecencyMs(result: gameResult): number {
  const kickoff = result.kickOffTime as { toMillis?: () => number } | undefined;
  const timestamp = result.timestamp as { toMillis?: () => number } | undefined;
  return kickoff?.toMillis?.() ?? timestamp?.toMillis?.() ?? 0;
}

function pickCanonicalRow(group: ScoredResultRow[]): ScoredResultRow {
  return [...group].sort((a, b) => {
    const priorityDiff = rowPriority(b.docId, b.result) - rowPriority(a.docId, a.result);
    if (priorityDiff !== 0) return priorityDiff;
    return rowRecencyMs(b.result) - rowRecencyMs(a.result);
  })[0]!;
}

export function dedupeGameResults(rows: ScoredResultRow[]): {
  results: gameResult[];
  duplicates: DuplicateResultGroup[];
} {
  const groups = new Map<string, ScoredResultRow[]>();

  for (const row of rows) {
    const key = dedupeMatchKey(row);
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }

  const results: gameResult[] = [];
  const duplicates: DuplicateResultGroup[] = [];

  for (const [matchKey, group] of groups) {
    const canonical = pickCanonicalRow(group);
    results.push(canonical.result);

    const dropped = group.filter((row) => row.docId !== canonical.docId).map((row) => row.docId);
    if (dropped.length > 0) {
      duplicates.push({ kept: canonical.docId, dropped, matchKey });
    }
  }

  return { results, duplicates };
}

/** Doc ids that would be dropped by dedupeGameResults (for Admin duplicate badges). */
export function findDuplicateResultDocIds(rows: ScoredResultRow[]): Set<string> {
  const duplicateDocIds = new Set<string>();
  const { duplicates } = dedupeGameResults(rows);
  for (const group of duplicates) {
    for (const docId of group.dropped) {
      duplicateDocIds.add(docId);
    }
  }
  return duplicateDocIds;
}

/** Doc ids in groups with more than one row (including the kept canonical row). */
export function findDuplicateResultGroupDocIds(rows: ScoredResultRow[]): Set<string> {
  const grouped = new Map<string, ScoredResultRow[]>();
  for (const row of rows) {
    const key = dedupeMatchKey(row);
    const bucket = grouped.get(key) ?? [];
    bucket.push(row);
    grouped.set(key, bucket);
  }

  const docIds = new Set<string>();
  for (const group of grouped.values()) {
    if (group.length <= 1) continue;
    for (const row of group) {
      docIds.add(row.docId);
    }
  }
  return docIds;
}
